import { supabase } from "~/integrations/supabase/client";

export interface StockMovementsFilters {
  dateFrom?: string;
  dateTo?: string;
  movementType?: string;
  productId?: string;
  userId?: string;
  search?: string;
}

export interface StockMovementRow {
  id: string;
  product_id: string;
  product_name: string | null;
  product_sku: string | null;
  movement_type: string;
  quantity_change: number;
  balance_after: number;
  reference_type: string | null;
  reference_id: string | null;
  reason: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  total_count: number;
}

export interface WmsMovementRow {
  id: string;
  movement_type: string;
  pallet_id: string | null;
  pallet_barcode: string | null;
  product_id: string;
  product_name: string | null;
  lot_number: string | null;
  box_quantity: number;
  from_position_id: string | null;
  from_position_code: string | null;
  to_position_id: string | null;
  to_position_code: string | null;
  reference_type: string | null;
  reference_id: string | null;
  reason: string | null;
  notes: string | null;
  performed_by: string | null;
  performed_by_name: string | null;
  performed_at: string;
}

export interface IngredientMovementRow {
  id: string;
  ingredient_type: string;
  ingredient_id: string | null;
  ingredient_name: string | null;
  ingredient_code: string | null;
  movement_type: string;
  quantity: number;
  unit: string;
  plant: string | null;
  balance_after: number;
  reference_type: string | null;
  reference_id: string | null;
  reference_number: string | null;
  unit_cost: number | null;
  total_cost: number | null;
  currency: string | null;
  notes: string | null;
  performed_by: string | null;
  performed_by_name: string | null;
  created_at: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface MovementUser {
  id: string;
  name: string;
}

export const movementsService = {
  /**
   * Get all stock movements with filters (non-paginated, for export)
   */
  async getAllStockMovements(
    filters: StockMovementsFilters
  ): Promise<StockMovementRow[]> {
    const { data, error } = await supabase.rpc("get_paginated_stock_movements", {
      p_date_from: filters.dateFrom || null,
      p_date_to: filters.dateTo || null,
      p_movement_type: filters.movementType || null,
      p_product_id: filters.productId || null,
      p_user_id: filters.userId || null,
      p_search: filters.search || null,
      p_page: 1,
      p_page_size: 100000, // Large number to get all records
    });

    if (error) throw error;

    return (data || []) as StockMovementRow[];
  },

  /**
   * Get paginated stock movements with filters
   * Uses RPC function for efficient server-side pagination
   */
  async getStockMovements(
    filters: StockMovementsFilters,
    page: number = 1,
    pageSize: number = 50
  ): Promise<PaginatedResponse<StockMovementRow>> {
    const { data, error } = await supabase.rpc("get_paginated_stock_movements", {
      p_date_from: filters.dateFrom || null,
      p_date_to: filters.dateTo || null,
      p_movement_type: filters.movementType || null,
      p_product_id: filters.productId || null,
      p_user_id: filters.userId || null,
      p_search: filters.search || null,
      p_page: page,
      p_page_size: pageSize,
    });

    if (error) throw error;

    const rows = (data || []) as StockMovementRow[];
    const totalCount = rows.length > 0 ? Number(rows[0].total_count) : 0;

    return {
      data: rows,
      totalCount,
      page,
      pageSize,
      totalPages: Math.ceil(totalCount / pageSize),
    };
  },

  /**
   * Get WMS movements with optional filters
   */
  async getWmsMovements(
    filters: StockMovementsFilters
  ): Promise<WmsMovementRow[]> {
    let query = supabase
      .from("wms_movements")
      .select(
        `
        id,
        movement_type,
        pallet_id,
        pallet:wms_pallets(barcode),
        product_id,
        product:products(short_name),
        lot_number,
        box_quantity,
        from_position_id,
        from_position:wms_positions!wms_movements_from_position_id_fkey(full_code),
        to_position_id,
        to_position:wms_positions!wms_movements_to_position_id_fkey(full_code),
        reference_type,
        reference_id,
        reason,
        notes,
        performed_by,
        performer:profiles!wms_movements_performed_by_fkey(name),
        performed_at
      `
      )
      .order("performed_at", { ascending: false });

    if (filters.dateFrom) {
      query = query.gte("performed_at", filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte("performed_at", filters.dateTo);
    }
    if (filters.movementType) {
      query = query.eq("movement_type", filters.movementType);
    }
    if (filters.productId) {
      query = query.eq("product_id", filters.productId);
    }
    if (filters.userId) {
      query = query.eq("performed_by", filters.userId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return (data || []).map((row) => ({
      id: row.id,
      movement_type: row.movement_type,
      pallet_id: row.pallet_id,
      pallet_barcode: (row.pallet as { barcode: string } | null)?.barcode || null,
      product_id: row.product_id,
      product_name: (row.product as { short_name: string } | null)?.short_name || null,
      lot_number: row.lot_number,
      box_quantity: row.box_quantity,
      from_position_id: row.from_position_id,
      from_position_code: (row.from_position as { full_code: string } | null)?.full_code || null,
      to_position_id: row.to_position_id,
      to_position_code: (row.to_position as { full_code: string } | null)?.full_code || null,
      reference_type: row.reference_type,
      reference_id: row.reference_id,
      reason: row.reason,
      notes: row.notes,
      performed_by: row.performed_by,
      performed_by_name: (row.performer as { name: string } | null)?.name || null,
      performed_at: row.performed_at,
    }));
  },

  /**
   * Get ingredient stock movements with optional filters
   */
  async getIngredientMovements(
    filters: StockMovementsFilters
  ): Promise<IngredientMovementRow[]> {
    let query = supabase
      .from("ingredient_stock_movements")
      .select(
        `
        id,
        ingredient_type,
        oil_type_id,
        oil_type:oil_types(name, code),
        raw_material_id,
        raw_material:raw_materials(name, code),
        movement_type,
        quantity,
        unit,
        plant,
        balance_after,
        reference_type,
        reference_id,
        reference_number,
        unit_cost,
        total_cost,
        currency,
        notes,
        performed_by,
        performer:profiles!ingredient_stock_movements_performed_by_fkey(name),
        created_at
      `
      )
      .order("created_at", { ascending: false });

    if (filters.dateFrom) {
      query = query.gte("created_at", filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte("created_at", filters.dateTo);
    }
    if (filters.movementType) {
      query = query.eq("movement_type", filters.movementType);
    }
    if (filters.userId) {
      query = query.eq("performed_by", filters.userId);
    }
    // Search by ingredient code OR name. ingredient_stock_movements only holds the
    // FK, so resolve the term against oils + raw materials first, then filter by id.
    if (filters.search?.trim()) {
      const term = `%${filters.search.trim()}%`;
      const [rmRes, oilRes] = await Promise.all([
        supabase
          .from("raw_materials")
          .select("id")
          .or(`code.ilike.${term},name.ilike.${term}`),
        supabase
          .from("oil_types")
          .select("id")
          .or(`code.ilike.${term},name.ilike.${term}`),
      ]);
      const rmIds = (rmRes.data || []).map((r) => r.id);
      const oilIds = (oilRes.data || []).map((o) => o.id);
      const orParts: string[] = [];
      if (rmIds.length) orParts.push(`raw_material_id.in.(${rmIds.join(",")})`);
      if (oilIds.length) orParts.push(`oil_type_id.in.(${oilIds.join(",")})`);
      // No ingredient matched the term -> no movements can match.
      if (orParts.length === 0) return [];
      query = query.or(orParts.join(","));
    }

    const { data, error } = await query;

    if (error) throw error;

    return (data || []).map((row) => ({
      id: row.id,
      ingredient_type: row.ingredient_type,
      ingredient_id: row.oil_type_id || row.raw_material_id,
      ingredient_name:
        (row.oil_type as { name: string } | null)?.name ||
        (row.raw_material as { name: string } | null)?.name ||
        null,
      ingredient_code:
        (row.oil_type as { code: string } | null)?.code ||
        (row.raw_material as { code: string } | null)?.code ||
        null,
      movement_type: row.movement_type,
      quantity: row.quantity,
      unit: row.unit,
      plant: row.plant,
      balance_after: row.balance_after,
      reference_type: row.reference_type,
      reference_id: row.reference_id,
      reference_number: row.reference_number,
      unit_cost: row.unit_cost,
      total_cost: row.total_cost,
      currency: row.currency,
      notes: row.notes,
      performed_by: row.performed_by,
      performed_by_name: (row.performer as { name: string } | null)?.name || null,
      created_at: row.created_at,
    }));
  },

  /**
   * Get distinct users who have performed movements
   * Used for filter dropdown
   */
  async getMovementUsers(): Promise<MovementUser[]> {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, name")
      .order("name");

    if (error) throw error;

    return (data || []).filter((u) => u.name) as MovementUser[];
  },

  /**
   * Get distinct movement types for stock movements
   */
  async getStockMovementTypes(): Promise<string[]> {
    const { data, error } = await supabase
      .from("stock_movements")
      .select("movement_type")
      .limit(1000);

    if (error) throw error;

    const types = new Set((data || []).map((r) => r.movement_type));
    return Array.from(types).sort();
  },
};
